import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { siteInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const WEB3FORMS_ACCESS_KEY = '6706ee60-a224-493c-aa18-36b2daf3b906';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: 'Bokning', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `[Ljungdalens Turridning] ${form.subject} — ${form.name}`,
          from_name: `${form.name} (Ljungdalens Turridning hemsida)`,
          name: form.name,
          email: form.email,
          ärende: form.subject,
          message: form.message,
          botcheck: '',
        }),
      });
      const data = await res.json();

      if (data.success) {
        setStatus('sent');
        toast({
          title: 'Tack för ditt meddelande!',
          description: 'Vi återkommer till dig så snart som möjligt.',
        });
        setTimeout(() => {
          setStatus('idle');
          setForm({ name: '', email: '', subject: 'Bokning', message: '' });
        }, 3500);
      } else {
        throw new Error(data.message || 'Något gick fel');
      }
    } catch (err) {
      setStatus('error');
      toast({
        title: 'Kunde inte skicka meddelandet',
        description: 'Försök igen, eller ring oss direkt på ' + siteInfo.phone,
        variant: 'destructive',
      });
      setTimeout(() => setStatus('idle'), 3500);
    }
  };

  const buttonContent = () => {
    if (status === 'sending') return (<><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Skickar...</>);
    if (status === 'sent') return (<><CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Skickat</>);
    return (<>Skicka meddelande <Send className="w-4 h-4" strokeWidth={1.5} /></>);
  };

  return (
    <section id="kontakt" className="py-24 md:py-36 bg-[#f1ece0] grain relative">
      <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
        <div className="text-center max-w-2xl mx-auto mb-16 reveal">
          <p className="text-[#8a8572] text-xs uppercase tracking-[0.4em] mb-5">— Kontakt</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2f3d30] leading-[1.05] mb-6">
            Boka din <em className="italic font-light">ridtur</em>.
          </h2>
          <p className="text-[#4a4a45] font-light leading-relaxed">
            Ring, maila eller fyll i formuläret nedan så återkommer vi med tillgängliga datum och detaljer.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 reveal">
          <div className="lg:col-span-2 space-y-4">
            <a
              href={`tel:${siteInfo.phone}`}
              className="block bg-[#faf7f2] border border-[#e8e1d2] p-7 hover:border-[#2f3d30] transition-colors group"
            >
              <Phone className="w-5 h-5 text-[#8a6a3a] mb-4" strokeWidth={1.5} />
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#8a8572] mb-2">Telefon</p>
              <p className="font-display text-2xl text-[#2f3d30] group-hover:text-[#8a6a3a] transition-colors">
                {siteInfo.phone}
              </p>
            </a>
            <a
              href={`mailto:${siteInfo.email}`}
              className="block bg-[#faf7f2] border border-[#e8e1d2] p-7 hover:border-[#2f3d30] transition-colors group"
            >
              <Mail className="w-5 h-5 text-[#8a6a3a] mb-4" strokeWidth={1.5} />
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#8a8572] mb-2">E-post</p>
              <p className="font-display text-xl text-[#2f3d30] group-hover:text-[#8a6a3a] transition-colors break-all">
                {siteInfo.email}
              </p>
            </a>
          </div>

          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 bg-[#faf7f2] border border-[#e8e1d2] p-8 md:p-10 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8a8572] mb-2">
                  Namn *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#d6ceba] focus:border-[#2f3d30] py-2 text-[#2f3d30] outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8a8572] mb-2">
                  E-post *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-[#d6ceba] focus:border-[#2f3d30] py-2 text-[#2f3d30] outline-none transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8a8572] mb-2">
                Ärende *
              </label>
              <select
                name="subject"
                value={form.subject}
                onChange={handleChange}
                className="w-full bg-transparent border-b border-[#d6ceba] focus:border-[#2f3d30] py-2 text-[#2f3d30] outline-none transition-colors"
              >
                <option>Bokning</option>
                <option>Allmänna frågor</option>
                <option>Längre turer / arrangemang</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8a8572] mb-2">
                Meddelande *
              </label>
              <textarea
                name="message"
                required
                rows={5}
                value={form.message}
                onChange={handleChange}
                className="w-full bg-transparent border-b border-[#d6ceba] focus:border-[#2f3d30] py-2 text-[#2f3d30] outline-none transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              className="btn-primary mt-4 disabled:opacity-70"
              disabled={status === 'sending' || status === 'sent'}
            >
              {buttonContent()}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
